// Qué puede entrar en una carga nueva y cuánto.
//
// El tope de una línea no es lo que queda por servir: es lo que queda por
// LANZAR, y se mide contra tres cosas que hay que restar en este orden —
//
//   pedida (de la LÍNEA)  −  lo ya lanzado en cargas  −  lo pedido y aún sin carga
//
// El tercer sumando es el que no se ve en ninguna pantalla: una carga solicitada
// tarda minutos en existir (la teclea un robot) y otros quince en llegar a
// Fabric. Sin restarlo, el usuario puede pedir dos veces la misma mercancía y las
// dos peticiones parecerán correctas.
//
// Los códigos de error son LOS MISMOS que devuelve el backend
// (`_validar_lineas` en routers/pedidos_ventas.py): el usuario tiene que leer el
// mismo mensaje venga de donde venga el rechazo.
import type {
  CargaLinea,
  CargaValidacionError,
  PedidoVentaLinea,
  SolicitudCarga,
} from '@/types'
import { agruparPorTransid } from './cargasPicking'

/** Una línea del pedido, con las cuentas hechas para el formulario. */
export interface DisponibleLinea {
  transid: string
  numLinea?: number
  articulo: string
  descripcion?: string
  /** Dimensiones, cuando se conocen: sólo las trae el picking existente. */
  color?: string
  estilo?: string
  /** Cantidad de la LÍNEA (qtyLinea), no de la fila. */
  pedida: number
  /** Solicitado en cargas ya existentes, esté picado o no. */
  lanzado: number
  /** Comprometido en solicitudes que aún no son carga. */
  reservado: number
  /** Lo que se puede meter en esta carga. Nunca negativo. */
  disponible: number
}

/** Selección del formulario: transid → cantidad. */
export type BorradorCarga = Map<string, number>

/** Cuánto tiene comprometido cada línea en solicitudes vivas. */
export function reservadoPorTransid(solicitudes: SolicitudCarga[]): Map<string, number> {
  const out = new Map<string, number>()
  for (const s of solicitudes) {
    for (const l of s.lineas) {
      out.set(l.inventtransid, (out.get(l.inventtransid) ?? 0) + l.cantidad)
    }
  }
  return out
}

/** Las líneas del pedido con su disponible, listas para pintar.
 *
 *  Deduplicadas por `inventtransid`: la consulta devuelve una fila por
 *  (línea × factura) y `qtyPedida` es de fila, así que la cantidad buena es
 *  `qtyLinea` — mismo criterio que `lanzamientoDe` en LineasRow.
 *
 *  Las líneas sin `inventtransid` se caen: sin esa clave no se pueden cruzar con
 *  las cargas ni validar en el servidor, y ofrecerlas sería ofrecer un error.
 */
export function disponiblePorLinea(
  lineas: PedidoVentaLinea[],
  cargas: CargaLinea[],
  solicitudes: SolicitudCarga[] = [],
): DisponibleLinea[] {
  const picking = agruparPorTransid(cargas)
  const reservado = reservadoPorTransid(solicitudes)
  // Las dimensiones no están en la línea de venta; se toman prestadas del picking
  // cuando la línea ya ha salido alguna vez en una carga.
  const dimensiones = new Map<string, { color?: string; estilo?: string }>()
  for (const c of cargas) {
    if (!dimensiones.has(c.transid)) {
      dimensiones.set(c.transid, { color: c.color, estilo: c.estilo })
    }
  }

  const vistos = new Set<string>()
  const out: DisponibleLinea[] = []
  for (const l of lineas) {
    const transid = l.inventtransid?.trim()
    if (!transid || vistos.has(transid)) continue
    vistos.add(transid)

    const pedida = l.qtyLinea
    const lanzado = picking.get(transid)?.solicitada ?? 0
    const res = reservado.get(transid) ?? 0
    const dim = dimensiones.get(transid)
    out.push({
      transid,
      numLinea: l.numLinea ?? undefined,
      articulo: l.articulo ?? '',
      descripcion: l.descripcion ?? undefined,
      color: dim?.color || undefined,
      estilo: dim?.estilo || undefined,
      pedida,
      lanzado,
      reservado: res,
      disponible: Math.max(0, pedida - lanzado - res),
    })
  }

  // Por número de línea, que es como se lee el pedido en el resto de la pantalla.
  return out.sort((a, b) => {
    if (a.numLinea == null && b.numLinea == null) {
      return a.articulo.localeCompare(b.articulo, 'es-ES')
    }
    if (a.numLinea == null) return 1
    if (b.numLinea == null) return -1
    return a.numLinea - b.numLinea || a.articulo.localeCompare(b.articulo, 'es-ES')
  })
}

// Una milésima de tolerancia: la cantidad pasa por SQL, por JSON y por un input
// de texto, y un 40.000000000000006 no es un exceso. Mismo margen que el backend.
const EPS = 1e-3

/** Todos los motivos por los que este borrador no se puede enviar.
 *
 *  Se devuelven TODOS y no el primero: corregir de uno en uno una carga de
 *  treinta líneas es un castigo.
 */
export function validarBorrador(
  borrador: BorradorCarga,
  disponibles: DisponibleLinea[],
): CargaValidacionError[] {
  const porTransid = new Map(disponibles.map(d => [d.transid, d]))
  const errores: CargaValidacionError[] = []

  for (const [transid, cantidad] of borrador) {
    const linea = porTransid.get(transid)
    if (!linea) {
      errores.push({ codigo: 'linea_ajena', inventtransid: transid })
      continue
    }
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      errores.push({
        codigo: 'cantidad_invalida', inventtransid: transid, articulo: linea.articulo,
      })
      continue
    }
    if (cantidad > linea.disponible + EPS) {
      errores.push({
        codigo: 'excede_disponible',
        inventtransid: transid,
        articulo: linea.articulo,
        valores: {
          solicitado: cantidad,
          disponible: linea.disponible,
          pedida: linea.pedida,
          lanzado: linea.lanzado,
          reservado: linea.reservado,
        },
      })
    }
  }

  return errores
}

/** Lo que se enseña en la barra de totales. */
export function totalBorrador(borrador: BorradorCarga) {
  let lineas = 0
  let unidades = 0
  for (const cantidad of borrador.values()) {
    if (!Number.isFinite(cantidad) || cantidad <= 0) continue
    lineas += 1
    unidades += cantidad
  }
  return { lineas, unidades }
}

/** ¿Hay algo que enviar y todo lo que hay es válido? */
export function borradorListo(
  borrador: BorradorCarga,
  disponibles: DisponibleLinea[],
): boolean {
  return totalBorrador(borrador).lineas > 0 && validarBorrador(borrador, disponibles).length === 0
}
